import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ReloadOutlined } from "@ant-design/icons";
import { Alert, Button, DatePicker, Input, Select, Table, Tooltip, message } from "antd";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { fetchSprTov } from "@components/health/clients/health-clients-api";
import GridSettings, {
  applyGridSettings,
  loadGridSettings,
  normalizeGridSettings,
  saveGridSettings
} from "@controls/grid-settings";
import { fetchWarehouseLookups } from "./warehouse-api";
import {
  PAGE_SIZE,
  formatDate,
  formatNumber,
  normalizeLookups,
  normalizePage,
  pick,
  toNumber
} from "./warehouse-utils";
import "./warehouse.css";

const normalizeProductOptions = (data) => {
  const rows = Array.isArray(data) ? data : pick(data, "items", "Items") || [];
  return rows
    .map((row) => ({
      value: toNumber(pick(row, "idTov", "IdTov", "id", "Id")),
      label: pick(row, "name", "Name", "productName", "ProductName") ?? ""
    }))
    .filter((row) => row.value != null);
};

const WarehouseLedger = ({
  kind,
  fetchPage,
  normalizeRow,
  columns: columnDefinitions,
  productFilter = false,
  sourceFilter = false,
  defaultSortBy = "fdate"
}) => {
  const navigate = useNavigate();
  const gridKey = `planOnline.warehouse.${kind}.grid`;
  const [filters, setFilters] = useState({
    dateBeg: dayjs().startOf("month").format("YYYY-MM-DD"),
    dateEnd: dayjs().format("YYYY-MM-DD"),
    idSkl: undefined,
    idTov: undefined,
    query: "",
    vidOsn: undefined,
    sortBy: defaultSortBy,
    sortDir: "desc"
  });
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [lookups, setLookups] = useState({ warehouses: [] });
  const [products, setProducts] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [scrollY, setScrollY] = useState(400);
  const [gridSettings, setGridSettings] = useState(() => loadGridSettings(gridKey));
  const bodyRef = useRef(null);
  const requestRef = useRef(0);
  const productTimerRef = useRef();
  const productRequestRef = useRef(0);
  const skipRef = useRef(0);
  const hasMoreRef = useRef(false);
  const loadingMoreRef = useRef(false);
  const loadRef = useRef();

  useEffect(() => {
    fetchWarehouseLookups()
      .then((data) => setLookups(normalizeLookups(data)))
      .catch((e) => message.error(e.message || "Не удалось загрузить склады"));
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(filters.query.trim()), 350);
    return () => clearTimeout(timer);
  }, [filters.query]);

  const loadProducts = (query = "") => {
    clearTimeout(productTimerRef.current);
    const requestId = ++productRequestRef.current;
    const selectedId = filters.idTov;
    productTimerRef.current = setTimeout(() => {
      fetchSprTov(query)
        .then((data) => {
          if (requestId !== productRequestRef.current) return;
          const next = normalizeProductOptions(data);
          setProducts((previous) => {
            const selected = previous.find((item) => item.value === selectedId);
            return selected && !next.some((item) => item.value === selectedId) ? [selected, ...next] : next;
          });
        })
        .catch(() => undefined);
    }, 300);
  };

  const loadPage = useCallback(
    async (reset) => {
      if (!reset && (loadingMoreRef.current || !hasMoreRef.current)) return;
      const requestId = ++requestRef.current;
      const skip = reset ? 0 : skipRef.current;
      if (reset) {
        setLoading(true);
        setError("");
      } else {
        loadingMoreRef.current = true;
        setLoadingMore(true);
      }
      try {
        const data = await fetchPage({
          skip,
          take: PAGE_SIZE,
          dateBeg: filters.dateBeg,
          dateEnd: filters.dateEnd,
          idSkl: filters.idSkl,
          idTov: productFilter ? filters.idTov : undefined,
          vidOsn: sourceFilter ? filters.vidOsn : undefined,
          query: debouncedQuery,
          sortBy: filters.sortBy,
          sortDir: filters.sortDir
        });
        if (requestId !== requestRef.current) return;
        const page = normalizePage(data, normalizeRow);
        setRows((previous) => (reset ? page.items : [...previous, ...page.items]));
        skipRef.current = skip + page.items.length;
        hasMoreRef.current = page.hasMore;
      } catch (e) {
        if (requestId !== requestRef.current) return;
        if (reset) {
          setRows([]);
          setError(e.message || "Не удалось загрузить данные");
        } else message.error(e.message || "Не удалось подгрузить данные");
      } finally {
        if (requestId === requestRef.current) {
          setLoading(false);
          setLoadingMore(false);
          loadingMoreRef.current = false;
        }
      }
    },
    [
      debouncedQuery,
      fetchPage,
      filters.dateBeg,
      filters.dateEnd,
      filters.idSkl,
      filters.idTov,
      filters.vidOsn,
      filters.sortBy,
      filters.sortDir,
      normalizeRow,
      productFilter,
      sourceFilter
    ]
  );
  loadRef.current = loadPage;

  useEffect(() => {
    loadPage(true);
  }, [loadPage]);

  useEffect(() => {
    const root = bodyRef.current;
    if (!root || typeof ResizeObserver === "undefined") return undefined;
    const update = () => setScrollY(Math.max(180, root.clientHeight - 40));
    update();
    const observer = new ResizeObserver(update);
    observer.observe(root);
    return () => observer.disconnect();
  }, [rows.length]);

  useEffect(() => {
    const body = bodyRef.current?.querySelector(".ant-table-body");
    if (!body) return undefined;
    const onScroll = () => {
      if (body.scrollTop + body.clientHeight >= body.scrollHeight - 80) loadRef.current?.(false);
    };
    body.addEventListener("scroll", onScroll);
    return () => body.removeEventListener("scroll", onScroll);
  }, [rows.length, scrollY]);

  const order = filters.sortDir === "desc" ? "descend" : "ascend";
  const allColumns = useMemo(
    () =>
      columnDefinitions.map((column) => ({
        ...column,
        sorter: true,
        sortOrder: filters.sortBy === (column.sortKey || column.key) ? order : null,
        sortIcon: ({ sortOrder }) => (!sortOrder ? null : sortOrder === "ascend" ? "▲" : "▼"),
        showSorterTooltip: false,
        ...(column.type === "date" ? { render: formatDate } : {}),
        ...(column.type === "number" ? { render: formatNumber } : {}),
        ...(column.type === "source"
          ? {
              render: (value, record) =>
                record.vidOsn === 1 && record.idOsn ? (
                  <Button
                    type="link"
                    size="small"
                    className="warehouse-source-link"
                    onClick={(event) => {
                      event.stopPropagation();
                      navigate(`/warehouse/documents/${record.idOsn}`);
                    }}
                  >
                    {value || `Накладная #${record.idOsn}`}
                  </Button>
                ) : value
            }
          : {})
      })),
    [columnDefinitions, filters.sortBy, navigate, order]
  );
  const resolvedSettings = useMemo(
    () => normalizeGridSettings(allColumns, gridSettings),
    [allColumns, gridSettings]
  );
  const columns = useMemo(
    () => applyGridSettings(allColumns, resolvedSettings),
    [allColumns, resolvedSettings]
  );

  return (
    <div className="warehouse-page">
      <div className="warehouse-toolbar">
        <DatePicker
          value={dayjs(filters.dateBeg)}
          allowClear={false}
          format="DD.MM.YYYY"
          onChange={(value) => setFilters((previous) => ({ ...previous, dateBeg: value.format("YYYY-MM-DD") }))}
        />
        <DatePicker
          value={dayjs(filters.dateEnd)}
          allowClear={false}
          format="DD.MM.YYYY"
          onChange={(value) => setFilters((previous) => ({ ...previous, dateEnd: value.format("YYYY-MM-DD") }))}
        />
        <Select
          allowClear
          showSearch
          optionFilterProp="label"
          placeholder="Склад"
          options={lookups.warehouses}
          value={filters.idSkl}
          onChange={(idSkl) => setFilters((previous) => ({ ...previous, idSkl }))}
          style={{ width: 200 }}
        />
        {productFilter ? (
          <Select
            allowClear
            showSearch
            filterOption={false}
            placeholder="Товар"
            options={products}
            value={filters.idTov}
            onSearch={loadProducts}
            onDropdownVisibleChange={(visible) => visible && loadProducts("")}
            onChange={(idTov) => setFilters((previous) => ({ ...previous, idTov }))}
            style={{ width: 240 }}
          />
        ) : null}
        {sourceFilter ? (
          <Select
            allowClear
            placeholder="Тип источника"
            value={filters.vidOsn}
            options={[{ value: 1, label: "Накладная" }]}
            onChange={(vidOsn) => setFilters((previous) => ({ ...previous, vidOsn }))}
            style={{ width: 180 }}
          />
        ) : null}
        <Input.Search
          allowClear
          placeholder="Поиск…"
          value={filters.query}
          onChange={(event) =>
            setFilters((previous) => ({ ...previous, query: event.target.value }))
          }
          style={{ width: 180 }}
        />
        <Tooltip title="Обновить">
          <Button icon={<ReloadOutlined />} loading={loading} onClick={() => loadPage(true)} />
        </Tooltip>
        <GridSettings
          columns={allColumns}
          storageKey={gridKey}
          value={resolvedSettings}
          onChange={(next) => {
            setGridSettings(next);
            saveGridSettings(gridKey, next);
          }}
        />
      </div>
      {error ? <Alert type="error" showIcon message={error} className="warehouse-error" /> : null}
      <div className="warehouse-table-wrap" ref={bodyRef}>
        <Table
          size="small"
          rowKey="key"
          columns={columns}
          dataSource={rows}
          pagination={false}
          loading={loading || loadingMore}
          scroll={{ x: Math.max(1000, columns.length * 150), y: scrollY }}
          sortDirections={["ascend", "descend"]}
          onChange={(_, __, sorter) => {
            const value = Array.isArray(sorter) ? sorter[0] : sorter;
            setFilters((previous) => ({
              ...previous,
              sortBy: value?.order
                ? value.column?.sortKey || value.columnKey
                : defaultSortBy,
              sortDir: value?.order === "ascend" ? "asc" : "desc"
            }));
          }}
        />
      </div>
    </div>
  );
};

export default WarehouseLedger;
